/**
 * LaunchCheckSection — single-screen launch-readiness runner.
 *
 * Walks a curated list of env-probe checks sequentially when "Run all" is
 * clicked. Each row flips green/red as it completes. Probes are read-only
 * (no on-chain writes, no SOL spend) so they're safe to spam on any cluster.
 * On-chain write tier is intentionally not in v1 — gate it behind a checkbox
 * + cluster guard before adding.
 */
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { LAMPORTS_PER_SOL, PublicKey } from "@solana/web3.js";
import React, { useCallback, useMemo, useRef, useState } from "react";

import { buildReadonlyProgram, getProgramId } from "../../explorer/anchor";
import {
  deriveRoundPda,
  deriveSeasonPda,
} from "../../games/last-touch/anchor";
import { KOTH_ROUND_LS_KEY } from "../../games/last-touch/useKothLobbyController";
import { LOBBY_PORT } from "@solblox/shared";
import { BN } from "@coral-xyz/anchor";
import { TestRow } from "../TestRow";
import { deriveTreasuryPda } from "../session";
import type { TestResult } from "../types";

const SEASON_LS_KEY = "solblox.season_id_hex";

type CheckId =
  | "wallet:connected"
  | "wallet:balance"
  | "rpc:slot"
  | "rpc:fresh"
  | "chain:treasury"
  | "chain:treasury-unpaused"
  | "chain:season"
  | "chain:season-unpaused"
  | "chain:round"
  | "chain:round-live"
  | "api:healthz"
  | "api:leaderboard"
  | "api:track"
  | "colyseus:reachable";

interface CheckDef {
  id: CheckId;
  label: string;
  hint: string;
  run: (ctx: RunCtx) => Promise<CheckOutcome>;
}

interface CheckOutcome {
  ok: boolean;
  output: string;
}

interface RunCtx {
  connection: ReturnType<typeof useConnection>["connection"];
  walletPubkey: PublicKey | null;
}

const blank: TestResult = { status: "idle" };

function apiBase(): string {
  const env = (import.meta as { env?: { VITE_API_URL?: string } }).env;
  return env?.VITE_API_URL ?? "http://localhost:3030";
}

function colyseusHttpBase(): string {
  const env = import.meta.env.VITE_COLYSEUS_URL as string | undefined;
  const ws =
    env && env.length > 0
      ? env
      : `ws://${typeof window !== "undefined" ? window.location.hostname : "127.0.0.1"}:${LOBBY_PORT}`;
  return ws.replace(/^ws/, "http").replace(/^wss/, "https");
}

const CHECKS: CheckDef[] = [
  {
    id: "wallet:connected",
    label: "Wallet connected",
    hint: "publicKey present from wallet-adapter",
    run: async ({ walletPubkey }) => {
      if (!walletPubkey) return { ok: false, output: "no wallet connected" };
      return { ok: true, output: walletPubkey.toBase58() };
    },
  },
  {
    id: "wallet:balance",
    label: "Wallet has ≥ 0.01 SOL",
    hint: "fee payer can afford a touch + rent",
    run: async ({ connection, walletPubkey }) => {
      if (!walletPubkey) return { ok: false, output: "no wallet" };
      const lamports = await connection.getBalance(walletPubkey);
      const sol = lamports / LAMPORTS_PER_SOL;
      const ok = lamports >= 0.01 * LAMPORTS_PER_SOL;
      return { ok, output: `${sol.toFixed(4)} SOL` };
    },
  },
  {
    id: "rpc:slot",
    label: "RPC reachable",
    hint: "getSlot returns a finite number",
    run: async ({ connection }) => {
      const slot = await connection.getSlot();
      if (!Number.isFinite(slot) || slot <= 0)
        return { ok: false, output: `bad slot ${slot}` };
      return { ok: true, output: `slot ${slot}` };
    },
  },
  {
    id: "rpc:fresh",
    label: "RPC producing blocks",
    hint: "slot advances within 3s",
    run: async ({ connection }) => {
      const before = await connection.getSlot();
      await new Promise((r) => setTimeout(r, 2500));
      const after = await connection.getSlot();
      const delta = after - before;
      if (delta <= 0)
        return { ok: false, output: `stuck — ${before} → ${after}` };
      return { ok: true, output: `+${delta} slots in 2.5s` };
    },
  },
  {
    id: "chain:treasury",
    label: "Treasury PDA initialized",
    hint: "fetch [treasury] account",
    run: async ({ connection }) => {
      const program = buildReadonlyProgram(connection);
      const pda = deriveTreasuryPda(getProgramId());
      const ns = (
        program.account as unknown as Record<
          string,
          { fetch: (pk: PublicKey) => Promise<unknown> }
        >
      )["treasury"];
      if (!ns) return { ok: false, output: "treasury namespace missing in IDL" };
      try {
        const acct = (await ns.fetch(pda)) as Record<string, unknown>;
        const admin = (acct.admin as PublicKey | undefined)?.toBase58?.() ?? "?";
        return {
          ok: true,
          output: `pda=${pda.toBase58().slice(0, 8)}… admin=${admin.slice(0, 8)}…`,
        };
      } catch (err) {
        return { ok: false, output: errMsg(err) };
      }
    },
  },
  {
    id: "chain:treasury-unpaused",
    label: "Treasury not paused",
    hint: "treasury.paused === false (kill switch)",
    run: async ({ connection }) => {
      const program = buildReadonlyProgram(connection);
      const pda = deriveTreasuryPda(getProgramId());
      const ns = (
        program.account as unknown as Record<
          string,
          { fetch: (pk: PublicKey) => Promise<unknown> }
        >
      )["treasury"];
      if (!ns) return { ok: false, output: "treasury ns missing" };
      try {
        const acct = (await ns.fetch(pda)) as Record<string, unknown>;
        const paused = acct.paused === true;
        return paused
          ? { ok: false, output: "PAUSED — admin_set_pause(false) to unpause" }
          : { ok: true, output: "not paused" };
      } catch (err) {
        return { ok: false, output: errMsg(err) };
      }
    },
  },
  {
    id: "chain:season",
    label: "Active season PDA exists",
    hint: `localStorage["${SEASON_LS_KEY}"] → Season PDA`,
    run: async ({ connection }) => {
      const hex =
        typeof localStorage !== "undefined"
          ? localStorage.getItem(SEASON_LS_KEY)
          : null;
      if (!hex || hex.length !== 64)
        return { ok: false, output: `season_id_hex not set in localStorage` };
      const bytes = hexToBytes(hex);
      if (!bytes) return { ok: false, output: "bad season hex" };
      const pda = deriveSeasonPda(bytes);
      const program = buildReadonlyProgram(connection);
      const ns = (
        program.account as unknown as Record<
          string,
          { fetch: (pk: PublicKey) => Promise<unknown> }
        >
      )["season"];
      if (!ns) return { ok: false, output: "season ns missing" };
      try {
        await ns.fetch(pda);
        return { ok: true, output: `pda=${pda.toBase58().slice(0, 12)}…` };
      } catch (err) {
        return { ok: false, output: errMsg(err) };
      }
    },
  },
  {
    id: "chain:season-unpaused",
    label: "Active season not paused",
    hint: "season.paused === false",
    run: async ({ connection }) => {
      const hex =
        typeof localStorage !== "undefined"
          ? localStorage.getItem(SEASON_LS_KEY)
          : null;
      if (!hex || hex.length !== 64)
        return { ok: false, output: "season hex missing" };
      const bytes = hexToBytes(hex);
      if (!bytes) return { ok: false, output: "bad season hex" };
      const pda = deriveSeasonPda(bytes);
      const program = buildReadonlyProgram(connection);
      const ns = (
        program.account as unknown as Record<
          string,
          { fetch: (pk: PublicKey) => Promise<unknown> }
        >
      )["season"];
      if (!ns) return { ok: false, output: "season ns missing" };
      try {
        const acct = (await ns.fetch(pda)) as Record<string, unknown>;
        return acct.paused === true
          ? { ok: false, output: "season PAUSED" }
          : { ok: true, output: "not paused" };
      } catch (err) {
        return { ok: false, output: errMsg(err) };
      }
    },
  },
  {
    id: "chain:round",
    label: "Active round PDA exists",
    hint: `localStorage["${KOTH_ROUND_LS_KEY}"] → Round PDA`,
    run: async ({ connection }) => {
      const raw =
        typeof localStorage !== "undefined"
          ? localStorage.getItem(KOTH_ROUND_LS_KEY)
          : null;
      if (!raw) return { ok: false, output: "round id missing" };
      let id: BN;
      try {
        id = new BN(raw, 10);
      } catch {
        return { ok: false, output: `bad round id "${raw}"` };
      }
      const pda = deriveRoundPda(id);
      const program = buildReadonlyProgram(connection);
      const ns = (
        program.account as unknown as Record<
          string,
          { fetch: (pk: PublicKey) => Promise<unknown> }
        >
      )["kothRound"];
      if (!ns) return { ok: false, output: "kothRound ns missing" };
      try {
        await ns.fetch(pda);
        return { ok: true, output: `round ${id.toString()}` };
      } catch (err) {
        return { ok: false, output: errMsg(err) };
      }
    },
  },
  {
    id: "chain:round-live",
    label: "Active round still live",
    hint: "expires_at > now (uses chain clock)",
    run: async ({ connection }) => {
      const raw =
        typeof localStorage !== "undefined"
          ? localStorage.getItem(KOTH_ROUND_LS_KEY)
          : null;
      if (!raw) return { ok: false, output: "round id missing" };
      let id: BN;
      try {
        id = new BN(raw, 10);
      } catch {
        return { ok: false, output: `bad round id "${raw}"` };
      }
      const pda = deriveRoundPda(id);
      const program = buildReadonlyProgram(connection);
      const ns = (
        program.account as unknown as Record<
          string,
          { fetch: (pk: PublicKey) => Promise<unknown> }
        >
      )["kothRound"];
      if (!ns) return { ok: false, output: "kothRound ns missing" };
      try {
        const acct = (await ns.fetch(pda)) as Record<string, unknown>;
        const expiresAt = bnLike(acct.expiresAt);
        if (expiresAt == null)
          return { ok: false, output: "round missing expires_at" };
        const slot = await connection.getSlot();
        const blockTime = await connection.getBlockTime(slot);
        if (blockTime == null)
          return { ok: false, output: "RPC returned no blockTime" };
        const remaining = expiresAt - blockTime;
        if (remaining <= 0)
          return { ok: false, output: `expired ${-remaining}s ago` };
        return { ok: true, output: `${remaining}s remaining (chain clock)` };
      } catch (err) {
        return { ok: false, output: errMsg(err) };
      }
    },
  },
  {
    id: "api:healthz",
    label: "API /healthz reachable",
    hint: `GET ${apiBase()}/healthz`,
    run: async () => {
      try {
        const res = await fetch(`${apiBase()}/healthz`, {
          headers: { "cache-control": "no-cache" },
        });
        if (!res.ok)
          return { ok: false, output: `HTTP ${res.status} ${res.statusText}` };
        const body = (await res.json()) as { ok?: boolean; launchId?: string };
        if (body.ok !== true)
          return { ok: false, output: `body.ok=${body.ok}` };
        return { ok: true, output: `launchId="${body.launchId ?? ""}"` };
      } catch (err) {
        return { ok: false, output: errMsg(err) };
      }
    },
  },
  {
    id: "api:leaderboard",
    label: "Leaderboard endpoint responds",
    hint: "/api/games/last-touch/all-time-kings",
    run: async () => {
      try {
        const res = await fetch(
          `${apiBase()}/api/games/last-touch/all-time-kings?limit=1`,
        );
        if (!res.ok)
          return { ok: false, output: `HTTP ${res.status}` };
        const body = (await res.json()) as { rows?: unknown[] };
        return {
          ok: true,
          output: `rows=${Array.isArray(body.rows) ? body.rows.length : "?"}`,
        };
      } catch (err) {
        return { ok: false, output: errMsg(err) };
      }
    },
  },
  {
    id: "api:track",
    label: "POST /api/track accepts events",
    hint: "funnel pipeline writeable",
    run: async () => {
      try {
        const res = await fetch(`${apiBase()}/api/track`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            type: "launch_check_probe",
            ts: Date.now(),
            session: "launch-check",
          }),
        });
        if (!res.ok)
          return { ok: false, output: `HTTP ${res.status}` };
        return { ok: true, output: "200 OK" };
      } catch (err) {
        return { ok: false, output: errMsg(err) };
      }
    },
  },
  {
    id: "colyseus:reachable",
    label: "Colyseus lobby reachable",
    hint: "matchmake HTTP probe",
    run: async () => {
      const base = colyseusHttpBase();
      try {
        // Any non-existent room gives a fast structured response — the body
        // doesn't matter, we only care that the lobby answered.
        const res = await fetch(
          `${base}/matchmake/joinById/__launch_check_probe__`,
          {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: "{}",
          },
        );
        if (res.status === 0)
          return { ok: false, output: "no response (CORS / down?)" };
        return { ok: true, output: `HTTP ${res.status} from ${base}` };
      } catch (err) {
        return { ok: false, output: errMsg(err) };
      }
    },
  },
];

function errMsg(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

function hexToBytes(hex: string): Uint8Array | null {
  if (hex.length % 2 !== 0) return null;
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    const b = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    if (!Number.isFinite(b)) return null;
    out[i] = b;
  }
  return out;
}

function bnLike(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === "number") return v;
  if (typeof (v as { toNumber?: () => number }).toNumber === "function") {
    try {
      return (v as { toNumber: () => number }).toNumber();
    } catch {
      return null;
    }
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function LaunchCheckSection(): JSX.Element {
  const { connection } = useConnection();
  const wallet = useWallet();
  const [results, setResults] = useState<Record<CheckId, TestResult>>(
    () => Object.fromEntries(CHECKS.map((c) => [c.id, blank])) as Record<CheckId, TestResult>,
  );
  const [stopOnFail, setStopOnFail] = useState<boolean>(false);
  const [runningAll, setRunningAll] = useState<boolean>(false);
  const abortRef = useRef<boolean>(false);

  const ctx = useMemo<RunCtx>(
    () => ({ connection, walletPubkey: wallet.publicKey ?? null }),
    [connection, wallet.publicKey],
  );

  const runOne = useCallback(
    async (id: CheckId) => {
      const def = CHECKS.find((c) => c.id === id);
      if (!def) return;
      setResults((r) => ({
        ...r,
        [id]: { status: "running", startedAt: Date.now() },
      }));
      try {
        const out = await def.run(ctx);
        setResults((r) => ({
          ...r,
          [id]: {
            status: out.ok ? "ok" : "fail",
            output: out.output,
            finishedAt: Date.now(),
          },
        }));
        return out.ok;
      } catch (err) {
        setResults((r) => ({
          ...r,
          [id]: {
            status: "fail",
            output: errMsg(err),
            finishedAt: Date.now(),
          },
        }));
        return false;
      }
    },
    [ctx],
  );

  const runAll = useCallback(async () => {
    setRunningAll(true);
    abortRef.current = false;
    // Reset all rows to idle so re-running shows clean progression.
    setResults(
      () =>
        Object.fromEntries(CHECKS.map((c) => [c.id, blank])) as Record<
          CheckId,
          TestResult
        >,
    );
    for (const def of CHECKS) {
      if (abortRef.current) break;
      const ok = await runOne(def.id);
      if (!ok && stopOnFail) break;
    }
    setRunningAll(false);
  }, [runOne, stopOnFail]);

  const stop = useCallback(() => {
    abortRef.current = true;
  }, []);

  const counts = useMemo(() => {
    let ok = 0;
    let fail = 0;
    let running = 0;
    let idle = 0;
    for (const c of CHECKS) {
      const s = results[c.id]?.status ?? "idle";
      if (s === "ok") ok++;
      else if (s === "fail") fail++;
      else if (s === "running") running++;
      else idle++;
    }
    return { ok, fail, running, idle, total: CHECKS.length };
  }, [results]);

  const aggregate = useMemo(() => {
    if (counts.running > 0)
      return { bg: "#23344f", fg: "#7ab0ff", label: "RUNNING…" };
    if (counts.fail > 0)
      return { bg: "#3a1c26", fg: "#f47a8e", label: `${counts.fail} FAIL` };
    if (counts.ok === counts.total)
      return { bg: "#1c3a26", fg: "#7af09e", label: "ALL GREEN" };
    if (counts.ok > 0)
      return { bg: "#2a2a36", fg: "#cfd2dc", label: `${counts.ok}/${counts.total}` };
    return { bg: "#22222e", fg: "#9aa0b4", label: "IDLE" };
  }, [counts]);

  return (
    <div style={{ padding: 12 }}>
      <div style={headerStyle}>
        <button
          onClick={runAll}
          disabled={runningAll}
          style={primaryBtnStyle(runningAll)}
        >
          {runningAll ? "Running…" : "Run all"}
        </button>
        <button
          onClick={stop}
          disabled={!runningAll}
          style={secondaryBtnStyle(!runningAll)}
        >
          Stop
        </button>
        <label style={toggleStyle}>
          <input
            type="checkbox"
            checked={stopOnFail}
            onChange={(e) => setStopOnFail(e.target.checked)}
            disabled={runningAll}
          />
          Stop on first fail
        </label>
        <div style={{ flex: 1 }} />
        <span style={pillStyle(aggregate)}>{aggregate.label}</span>
        <span style={countLineStyle}>
          {counts.ok} ok · {counts.fail} fail · {counts.idle} idle
        </span>
      </div>

      <div style={noteStyle}>
        Read-only probes. No on-chain writes, no SOL spent. Run on any cluster.
      </div>

      <div style={listStyle}>
        {CHECKS.map((def) => (
          <TestRow
            key={def.id}
            label={def.label}
            hint={def.hint}
            result={results[def.id]}
            onRun={() => {
              void runOne(def.id);
            }}
            disabled={runningAll}
          />
        ))}
      </div>
    </div>
  );
}

const headerStyle: React.CSSProperties = {
  display: "flex",
  gap: 10,
  alignItems: "center",
  marginBottom: 8,
  flexWrap: "wrap",
};

const noteStyle: React.CSSProperties = {
  color: "#7a8098",
  fontSize: 11,
  marginBottom: 10,
};

const listStyle: React.CSSProperties = {
  border: "1px solid #1c1c26",
  borderRadius: 6,
  overflow: "hidden",
};

function primaryBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: "6px 14px",
    background: disabled ? "#22222e" : "#1c3a5e",
    color: disabled ? "#5a5a72" : "#cfe3ff",
    border: "1px solid #2a4a72",
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
  };
}

function secondaryBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: "6px 12px",
    background: disabled ? "#22222e" : "#3a1c26",
    color: disabled ? "#5a5a72" : "#f4b0c0",
    border: "1px solid #5a2a36",
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
  };
}

const toggleStyle: React.CSSProperties = {
  color: "#cfd2dc",
  fontSize: 11,
  display: "flex",
  alignItems: "center",
  gap: 6,
};

function pillStyle(t: {
  bg: string;
  fg: string;
  label: string;
}): React.CSSProperties {
  return {
    background: t.bg,
    color: t.fg,
    padding: "2px 10px",
    borderRadius: 10,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.5,
  };
}

const countLineStyle: React.CSSProperties = {
  color: "#9aa0b4",
  fontSize: 11,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
};
